import {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";

import {
  useGetCDKCodDefaultValues,
  useGetCDKUniqueCODValues,
  usePostNewCDKCODDefaultSetting,
  useUpdateCDKCODDefaultSetting,
} from "../cdkSettingsPage.utils";

import AutoComplete from "../../../AutoComplete";
import Checkbox from "../../../Checkbox";

const formatcurrentCODValues = (CODDefaultData) => {
  if (!CODDefaultData) return [];

  return CODDefaultData.map((item) => ({
    label: item.invSaleType,
    value: item.invSaleType,
    isActive: item.isActive,
    id: item.id,
  }));
};

const formatUniqueCODValues = (uniqueCODData) => {
  if (!uniqueCODData) return [];

  const filterStringValues = uniqueCODData.filter(
    (item) => typeof item === "string"
  );

  const formatCODValues = filterStringValues.map((item) => ({
    label: item,
    value: item,
  }));
  return formatCODValues;
};

const DefaultCODField = forwardRef(function CODFilter(_props, ref) {
  const { data: CODDefaultData, isLoading: isCODDefaultDataLoading } =
    useGetCDKCodDefaultValues();

  const { data: uniqueCODValuesData, isLoading: isUniqueCODValuesDataLoading } =
    useGetCDKUniqueCODValues();

  const codMutationUpdate = useUpdateCDKCODDefaultSetting();

  const codMutationPost = usePostNewCDKCODDefaultSetting();

  const [currentCODValues, setCurrentCODValues] = useState([]);

  useEffect(() => {
    if (!CODDefaultData || typeof CODDefaultData === "string") return; // in case response is a string

    const visibleDefaultCODFilters = formatcurrentCODValues(
      CODDefaultData
    ).filter((filter) => filter.isActive);

    setCurrentCODValues(visibleDefaultCODFilters);
  }, [CODDefaultData]);

  const handleSaveAutoCompleteCOD = useCallback((options) => {
    if (!options) return;

    const newFilterOptions = options.length > 0 ? options : [];

    setCurrentCODValues(newFilterOptions);
  }, []);

  const handleOnSubmitCOD = () => {
    if (isUniqueCODValuesDataLoading || isCODDefaultDataLoading) return; // Prevents saving when data is still loading

    if (!currentCODValues?.length === 0) return; // Prevents saving when currentCODValues is empty

    const previousCODDefaultValues = formatcurrentCODValues(CODDefaultData);
    const currentAutoCompleteCODValues = currentCODValues?.map(
      (item) => item?.value
    );

    // Post new COD via filter names to the database
    const newCODOptions = currentAutoCompleteCODValues
      ?.filter(
        (item) =>
          !previousCODDefaultValues.some((filter) => filter.value === item)
      )
      .map((item) => ({ invSaleType: item, isActive: true }));

    // Post new ship via filter names to the database

    if (newCODOptions.length > 0) {
      codMutationPost.mutate(newCODOptions);
    }

    // Update COD via filter names to the database

    const deselectedCODOptions = previousCODDefaultValues
      ?.filter(
        (prevCODDefaultValue) =>
          !currentAutoCompleteCODValues?.includes(prevCODDefaultValue.value) &&
          prevCODDefaultValue.isActive
      )
      .map((item) => ({
        invSaleType: item.value,
        activeStatus: false,
      }));

    const selectedCODOptions = previousCODDefaultValues
      ?.filter(
        (prevCODDefaultValue) =>
          currentAutoCompleteCODValues?.includes(prevCODDefaultValue.value) &&
          !prevCODDefaultValue.isActive
      )
      ?.map((item) => ({
        invSaleType: item.value,
        activeStatus: true,
      }));

    const updateCODDefaults = [...deselectedCODOptions, ...selectedCODOptions];

    if (updateCODDefaults.length > 0) {
      codMutationUpdate.mutate(updateCODDefaults);
    }
  };

  useImperativeHandle(ref, () => ({
    handleOnSubmitCOD,
  }));

  return (
    <AutoComplete
      multiple
      freesolo
      options={formatUniqueCODValues(uniqueCODValuesData || [])}
      defaultValue={currentCODValues}
      getOptionLabel={(option) => option?.label || ""}
      handleBlurSave={handleSaveAutoCompleteCOD}
      noOptionsText="No options found, ENTER a new COD filter value"
      loading={isCODDefaultDataLoading || isUniqueCODValuesDataLoading}
      renderOption={(props, option, selected) => (
        <li {...props}>
          <Checkbox
            inputClassName="form-check-input"
            name={option.value || option.label}
            label={option.label}
            checked={selected}
            readOnly
          />
        </li>
      )}
      renderInput={(params) => <input {...params} placeholder="Select COD" />}
    />
  );
});

export default DefaultCODField;
